import React from 'react';
import {injectIntl} from 'react-intl';
import {Redirect} from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';

import API from '../../common/utils/API';
import {formatErrorMessageFromAPI} from '../../common/utils/helpers';
import Form from '../../components/form/categories/CategoryForm';
import Category from '../../common/models/Category';
import ErrorModal from '../../components/ui/ErrorModal';

export class EditCategory extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            category: new Category(),

            // Categories orders available
            categoriesOrder: [],

            // Redirects
            shouldRedirectToIndex: false,
            shouldRedirectToDetail: false,
            redirectSuccessMessage: '',

            // Errors
            isLoadErrorModalOpen: false,
            errorMessage: '',

            // Loading
            loading: false,
        };
    }

    async componentDidMount() {
        await Promise.all([this.fetchCategory(), this.fetchCategories()]);
    }

    fetchCategory = async () => {
        this.setState({loading: true});

        try {
            const {category} = await API.categories.detail(
                this.props.match.params.id,
            );

            this.setState({
                category,
                loading: false,
            });
        } catch (e) {
            this.setState({
                isLoadErrorModalOpen: true,
                loading: false,
            });
        }
    };

    fetchCategories = async () => {
        this.setState({loading: true});

        try {
            const {categories} = await API.categories.getAll();
            let categoriesOrder = [{value: 1, label: 1}];

            /* istanbul ignore else */
            if (categories.length) {
                categoriesOrder = categories.map((category) => ({
                    value: category.order,
                    label: category.order,
                }));
            }
            this.setState({
                categoriesOrder,
                loading: false,
            });
        } catch (e) {
            this.setState({
                errorMessage: this.props.intl.formatMessage({
                    id: 'ERROR_PAGES.LOADING_DATA',
                }),
                loading: false,
            });
        }
    };

    onSubmit = async (input) => {
        const formData = new FormData();

        this.setState({loading: true});

        formData.append('name', input.name);
        formData.append('order', input.order);

        /* istanbul ignore else */
        if (input.picture?.length) {
            formData.append('picture', input.picture[0]);
        }

        try {
            await API.categories.update(this.props.match.params.id, formData);

            this.setState({
                shouldRedirectToDetail: true,
                loading: false,
                redirectSuccessMessage: this.props.intl.formatMessage({
                    id: 'CATEGORIES.CATEGORY_SUCCESSFULLY_UPDATED_MESSAGE',
                }),
            });
        } catch (e) {
            const error = formatErrorMessageFromAPI(
                e,
                this.props.intl.formatMessage({
                    id: 'ERROR_PAGES.UPDATING_DATA',
                }),
            );

            this.setState({
                ...error,
            });
        }
    };

    handleDeleteCategory = async () => {
        this.setState({loading: true});

        try {
            await API.categories.delete(this.props.match.params.id);

            this.setState({
                loading: false,
                shouldRedirectToIndex: true,
                redirectSuccessMessage: this.props.intl.formatMessage({
                    id: 'CATEGORIES.CATEGORY_SUCCESSFULLY_DELETED_MESSAGE',
                }),
            });
        } catch (e) {
            this.setState({
                loading: false,
                errorMessage: this.props.intl.formatMessage({
                    id: 'ERROR_PAGES.DELETING_DATA',
                }),
            });
        }
    };

    toggleLoadErrorModal = () => {
        this.setState({
            isLoadErrorModalOpen: !this.state.isLoadErrorModalOpen,
        });
    };

    render() {
        const {
            shouldRedirectToDetail,
            shouldRedirectToIndex,
            redirectSuccessMessage: successMessage,
        } = this.state;

        const initialValues = {
            name: this.state.category?.name || '',
            order: this.state.category?.order || '',
            picture: [],
        };

        return shouldRedirectToDetail ? (
            <Redirect
                data-test='redirectToDetail'
                to={{
                    pathname: `/categories/${this.props.match.params.id}`,
                    state: {
                        successMessage,
                    },
                }}
            />
        ) : shouldRedirectToIndex ? (
            <Redirect
                data-test='redirectToIndex'
                to={{
                    pathname: '/categories',
                    state: {
                        successMessage: this.props.intl.formatMessage({
                            id:
                                'CATEGORIES.CATEGORY_SUCCESSFULLY_CREATED_MESSAGE',
                        }),
                    },
                }}
            />
        ) : (
            <>
                <Alert
                    variant='danger'
                    className='mb-4'
                    show={!!this.state.errorMessage}
                >
                    <p className='mb-0'>{this.state.errorMessage}</p>
                </Alert>
                <Form
                    data-test='updateCategoryComponent'
                    initialValues={initialValues}
                    backRedirectUrl={`/categories/${this.props.match.params.id}`}
                    backText={this.props.intl.formatMessage({
                        id: 'GENERAL.BACK_TO_DETAIL',
                    })}
                    categoriesOrder={this.state.categoriesOrder}
                    preview={this.state.category.iconUrl}
                    isEdit={true}
                    loading={this.state.loading}
                    handleSubmit={this.onSubmit}
                    handleDeleteCategory={this.handleDeleteCategory}
                />
                <ErrorModal
                    isLoadErrorModalOpen={this.state.isLoadErrorModalOpen}
                    toggleLoadErrorModal={this.toggleLoadErrorModal}
                    errorMessage={this.props.intl.formatMessage({
                        id: 'CATEGORIES.ERROR_MODAL_TEXT',
                    })}
                    returnUrl={`/categories/${this.props.match.params.id}`}
                    loading={this.state.loading}
                    handleRetryClick={this.fetchCategory}
                />
            </>
        );
    }
}

export default injectIntl(EditCategory);
